/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { IconButton, SvgIcon } from "@mui/material";
import { FC } from "react";
import { ReactComponent as cameraFrontLeft } from "./assets/camera_frontLeft.svg";
import { ReactComponent as cameraFrontRight } from "./assets/camera_frontRight.svg";
import { ReactComponent as cameraLeft } from "./assets/camera_Left.svg";
import { ReactComponent as cameraRight } from "./assets/camera_Right.svg";
import { ReactComponent as cameraRear } from "./assets/camera_Rear.svg";
import { ReactComponent as cameraFrontLeftActive } from "./assets/camera_frontLeft_active.svg";
import { ReactComponent as cameraFrontRightActive } from "./assets/camera_frontRight_active.svg";
import { ReactComponent as cameraLeftActive } from "./assets/camera_Left_active.svg";
import { ReactComponent as cameraRightActive } from "./assets/camera_Right_active.svg";
import { ReactComponent as cameraRearActive } from "./assets/camera_Rear_active.svg";

interface Props {
  cameraNo: number;
  position: CameraPosition;
  isCurrent?: boolean;
  width?: number | string;
  height?: number | string;
  onClick?: (cameraNo: number) => void;
}

export type CameraPosition =
  | "FrontLeft"
  | "FrontRight"
  | "Left"
  | "Right"
  | "Rear";

const buttonShapeMap: { [position: string]: ButtonShape } = {
  FrontLeft: {
    normal: cameraFrontLeft,
    active: cameraFrontLeftActive,
  },
  FrontRight: {
    normal: cameraFrontRight,
    active: cameraFrontRightActive,
  },
  Left: {
    normal: cameraLeft,
    active: cameraLeftActive,
  },
  Right: {
    normal: cameraRight,
    active: cameraRightActive,
  },
  Rear: {
    normal: cameraRear,
    active: cameraRearActive,
  },
};

/**
 * Component of camera selector button
 * @param{Props} Props Properties
 * @return{FC} Component
 */
export const CameraSelector: FC<Props> = ({
  cameraNo,
  position,
  isCurrent,
  width,
  height,
  onClick,
}: Props) => {
  const handleClick = () => {
    if (onClick) {
      onClick(cameraNo);
    }
  };
  return (
    <IconButton
      onClick={handleClick}
      sx={{ width: width ?? "100%", height: height ?? "100%", padding: 0 }}
      disableRipple
    >
      <SvgIcon
        component={
          isCurrent
            ? buttonShapeMap[position as string].active
            : buttonShapeMap[position as string].normal
        }
        sx={{ width: width ?? "100%", height: height ?? "100%" }}
        inheritViewBox
      />
    </IconButton>
  );
};

interface ButtonShape {
  normal: FC;
  active: FC;
}
